/**
 * <SegmentedBar> — a proportional multi-segment bar (atom, L0.1).
 *
 * A rounded track filled by contiguous colored segments, each `value/max` wide — so the total fill is
 * `sum(values)/max` split by color. Drives the legacy engagement bar (Présence / Participation /
 * Productivité over 100). No hex: each segment color is a `SemanticColorKey`; the track is `surface-subtle`.
 *
 * `gap` en fait la **barre segmentée** du livre (chap. 16, « Barre d'XP segmentée ») : des pastilles
 * séparées, allumées puis éteintes, au lieu d'une piste continue. Les éteintes sont alors des
 * segments comme les autres : `stepSegments(total, done)` les compose.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { type SpacingKey } from "../../theme/tokens/spacing"

export type BarSegment = { value: number; color: SemanticColorKey }

export type SegmentedBarProps = {
    segments: BarSegment[]
    /** Value corresponding to a full bar. */
    max: number
    /** Bar thickness in px — defaults to 12. */
    height?: number
    /**
     * L'écart entre deux segments : chacun devient une pastille à part, et la piste disparaît.
     * Absent (défaut) : la piste continue d'avant.
     */
    gap?: SpacingKey
    testID?: string
}

/** L'épaisseur de la barre segmentée du livre : plus fine que la piste continue. */
export const STEP_BAR_HEIGHT = 8

/**
 * Les segments d'une progression par étapes : `done` allumés sur `total`, le reste éteint.
 * `color` vaut `role`, la couleur de l'univers (« corail pour l'action ») ; une étape acquise
 * passe `success` (« vert pour l'acquis »). Les éteints prennent le contour du livre, qui se lit
 * sur une carte comme sur le fond de page.
 */
export function stepSegments(
    total: number,
    done: number,
    color: SemanticColorKey = "role",
    offColor: SemanticColorKey = "border"
): BarSegment[] {
    const count = Math.max(0, Math.floor(total))
    const lit = Math.max(0, Math.min(count, Math.floor(done)))
    return Array.from({ length: count }, (_, index) => ({ value: 1, color: index < lit ? color : offColor }))
}

export function SegmentedBar(props: SegmentedBarProps) {
    const { segments, max, height = 12, gap, testID } = props

    if (gap != null) {
        return (
            <View testID={testID} style={styles.steps(height, gap)}>
                {segments.map((segment, index) => (
                    <View key={index} style={styles.step(segment.color, Math.max(0, segment.value))} />
                ))}
            </View>
        )
    }

    return (
        <View testID={testID} style={styles.track(height)}>
            {segments.map((segment, index) => {
                const pct = max > 0 ? Math.max(0, Math.min(100, (segment.value / max) * 100)) : 0
                return <View key={index} style={styles.segment(segment.color, pct)} />
            })}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    track: (height: number) => ({
        flexDirection: "row" as const,
        width: "100%" as const,
        height,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["surface-subtle"],
        overflow: "hidden" as const,
    }),
    segment: (color: SemanticColorKey, pct: number) => ({
        width: `${pct}%` as `${number}%`,
        height: "100%" as const,
        backgroundColor: theme.colors[color],
    }),
    steps: (height: number, gap: SpacingKey) => ({
        flexDirection: "row" as const,
        width: "100%" as const,
        height,
        gap: theme.spacing[gap],
    }),
    /** Une pastille : sa part de la largeur, arrondie à elle seule. */
    step: (color: SemanticColorKey, value: number) => ({
        flexGrow: value,
        flexShrink: 1,
        flexBasis: 0,
        height: "100%" as const,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors[color],
    }),
}))
